import type { ReactNode } from "react";

export function PageHero({
  eyebrow,
  title,
  description,
  image,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  image?: string;
}) {
  return (
    <section className="relative overflow-hidden border-b border-border bg-navy text-primary-foreground">
      <div className="absolute inset-x-0 top-0 h-1.5 sky-bar" />
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-sky/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
        {eyebrow && (
          <div className="flex items-center gap-3">
            <span aria-hidden className="h-5 w-1.5 rounded-full bg-sky" />
            <p className="font-condensed text-xs font-bold uppercase tracking-[0.3em] text-sky">
              {eyebrow}
            </p>
          </div>
        )}
        <h1 className="mt-4 max-w-3xl font-stadium leading-[0.92] text-primary-foreground text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem]">
          {title}
        </h1>
        {description && (
          <p className="mt-5 max-w-2xl text-base text-primary-foreground/80 md:text-lg">
            {description}
          </p>
        )}
        <div aria-hidden className="mt-8 h-px w-24 bg-sky/60" />
      </div>
    </section>
  );
}

export function Section({
  id,
  title,
  eyebrow,
  children,
  description,
}: {
  id?: string;
  title?: string;
  eyebrow?: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
      {(title || eyebrow) && (
        <div className="mb-8 flex max-w-3xl flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-primary" />
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          </div>
          {title && (
            <h2 className="font-display text-3xl tracking-tight text-navy md:text-4xl">
              {title}
            </h2>
          )}
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>
      )}
      {children}
    </section>
  );
}
